import React, { memo, useCallback } from "react";
import { SecurityIssue } from "../../types/security";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { getSeverityColor, formatRiskScore, getIssueStatusColor } from "@/utils/securityHelpers";
import { cn } from "@/lib/utils";
import { useTenant } from "@/providers/TenantProvider";

interface SecurityIssuesListProps {
  issues: SecurityIssue[];
  onIssueClick: (issue: SecurityIssue) => void;
}

interface SecurityIssueRowProps {
  issue: SecurityIssue;
  onIssueClick: (issue: SecurityIssue) => void;
  tenantName?: string;
}

const SecurityIssueRow = memo(
  function SecurityIssueRow({ issue, onIssueClick, tenantName }: SecurityIssueRowProps) {
    const handleClick = useCallback(() => {
      onIssueClick(issue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [issue.id, onIssueClick]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onIssueClick(issue);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [issue.id, onIssueClick]
    );

    const clampedRiskScore = Math.max(0, Math.min(1, issue.riskScore));
    const riskScorePercentage = clampedRiskScore * 100;
    const isSuppressed = issue.status === "SUPPRESSED";

    return (
      <TableRow
        role="button"
        tabIndex={0}
        aria-label={`View details for ${issue.title}`}
        className={cn(
          "cursor-pointer hover:bg-muted/50 focus:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-all duration-200",
          isSuppressed && "border-l-2 border-l-purple-400 bg-purple-50/30 dark:bg-purple-950/20 text-muted-foreground"
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <TableCell className="font-medium text-xs truncate overflow-hidden">
          {issue.title}
        </TableCell>
        {tenantName !== undefined && (
          <TableCell className="text-xs text-muted-foreground">{tenantName}</TableCell>
        )}
        <TableCell>
          <Badge className={`${getSeverityColor(issue.severity)} rounded-[5px] text-xs`}>
            {issue.severity}
          </Badge>
        </TableCell>
        <TableCell>
          <Badge
            variant="outline"
            className={`${getIssueStatusColor(issue.status)} rounded-[5px] text-xs`}
          >
            {issue.status.replace(/_/g, " ")}
          </Badge>
        </TableCell>
        <TableCell className="text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${
                  clampedRiskScore >= 0.8
                    ? "bg-red-600"
                    : clampedRiskScore >= 0.6
                      ? "bg-orange-500"
                      : clampedRiskScore >= 0.4
                        ? "bg-yellow-500"
                        : "bg-green-500"
                }`}
                style={{ width: `${riskScorePercentage}%` }}
              />
            </div>
            <span className="font-medium">{formatRiskScore(clampedRiskScore)}</span>
          </div>
        </TableCell>
      </TableRow>
    );
  },
  (prevProps, nextProps) =>
    prevProps.issue.id === nextProps.issue.id &&
    prevProps.issue.title === nextProps.issue.title &&
    prevProps.issue.severity === nextProps.issue.severity &&
    prevProps.issue.status === nextProps.issue.status &&
    prevProps.issue.riskScore === nextProps.issue.riskScore &&
    prevProps.onIssueClick === nextProps.onIssueClick &&
    prevProps.tenantName === nextProps.tenantName
);

export const SecurityIssuesList: React.FC<SecurityIssuesListProps> = memo(function SecurityIssuesList({ issues, onIssueClick }) {
  const { isMssp, isAll, orgNameMap } = useTenant();
  const showTenantCol = isMssp && isAll;

  return (
    <div className="overflow-hidden rounded-[15px] border bg-white">
      <Table className="table-fixed w-full">
        <TableHeader>
          <TableRow>
            <TableHead className={`text-xs ${showTenantCol ? "w-[40%]" : "w-[50%]"}`}>Title</TableHead>
            {showTenantCol && <TableHead className="text-xs w-[12%]">Tenant</TableHead>}
            <TableHead className="text-xs w-[13%]">Severity</TableHead>
            <TableHead className="text-xs w-[13%]">Status</TableHead>
            <TableHead className="text-xs w-[18%]">Risk Score</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {issues.map((issue) => (
            <SecurityIssueRow
              key={issue.id}
              issue={issue}
              onIssueClick={onIssueClick}
              tenantName={showTenantCol ? orgNameMap.get(issue.organizationId ?? 0) : undefined}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
});
